import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ListGroup,
  Badge,
  Spinner,
  Alert,
  Table,
  Form,
} from 'react-bootstrap';
import { PersonPlus, CheckCircle, XCircle } from 'react-bootstrap-icons';
import DropdownSelect from '../Generic/DropdownSelect';
import { getUsersForOrder } from '../../clients/user_client';
import { getSupplierSelectList } from '../../clients/supplier_client';
import { logInfo, logWarn, downloadLogFile } from '../../config_and_helpers/logger';
import {
  findUserOption,
  withOptionalNoUser,
} from '../../config_and_helpers/userDropdownOptions';
import './QuoteUploadReviewStep.css';

const LOG_CONTEXT = 'QuoteUploadReviewStep';

const NO_PROJECT_OPTION = { value: '', label: 'No project' };

const mapUserToOption = user => {
  if (!user || user.id == null || String(user.id).trim() === '') {
    return null;
  }

  const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const fallbackEmail = (user.email || '').trim();
  const fallbackUsername = (user.username || '').trim();
  const label =
    displayName ||
    fallbackEmail ||
    fallbackUsername ||
    `User ${String(user.id).trim()}`;

  return {
    value: user.id,
    label,
  };
};

/**
 * @param {Array<{ value: *, label: string }>|undefined|null} options
 * @param {*} selectedValue
 * @returns {{ value: *, label: string }|null}
 */
function findProjectOption(options, selectedValue) {
  const list = Array.isArray(options) ? options : [];
  return (
    list.find(o => String(o.value) === String(selectedValue ?? '')) ?? null
  );
}

/**
 * Review step for bulk quote upload: shows parsed quote payloads and lets the user
 * assign an optional associated user to each quote (for email updates), then confirm or cancel.
 *
 * For PDF split-batch uploads, optional `serverOnlyFiles` lists PDFs where demand was not detected
 * in the browser scan; those rows show a server-processing message and optional user assignment.
 * `onConfirm` then receives `(quotesWithUsers, meta)` where `meta.serverUserIds` aligns with
 * `serverOnlyFiles` order when that prop is non-empty.
 *
 * @param {Object} props
 * @param {Object[]} props.quotePayloads - Array of quote payloads from parser (requisition_number, supplier_id, items, etc.)
 * @param {File[]} [props.serverOnlyFiles] - PDFs uploaded without client-parsed lines (same order as selected for server batch).
 * @param {string} props.token - Auth token
 * @param {Function} props.onConfirm - Called with quotesWithUsers; if serverOnlyFiles set, second arg `{ serverUserIds }`.
 * @param {Function} props.onCancel - Called when user cancels (back to file selection)
 * @param {boolean} props.disabled - Disable confirm/cancel
 * @param {string} props.confirmLabel - Label for confirm button (default: "Create quotes")
 * @param {boolean} props.isSubmitting - Show loading on confirm button
 * @param {string} [props.submitStatusMessage] - Optional stage message shown while submit is in-flight
 * @param {string} props.submitError - Error message to show above buttons
 * @param {string} [props.reviewHeading] - Optional section title override
 * @param {Array<{ value: *, label: string }>} [props.projectOptions]
 * @param {number|string|null} [props.defaultProjectId] - Prefill each row when review opens
 */
const QuoteUploadReviewStep = ({
  quotePayloads,
  serverOnlyFiles,
  token,
  onConfirm,
  onCancel,
  disabled = false,
  confirmLabel = 'Create quotes',
  isSubmitting = false,
  submitStatusMessage = '',
  submitError = '',
  reviewHeading,
  projectOptions = [],
  defaultProjectId = null,
}) => {
  const [userSelectList, setUserSelectList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  /** Per-quote editable requisition (demand) number — overrides parser when confirming. */
  const [editedRequisitions, setEditedRequisitions] = useState([]);
  /** Per-quote inner supplier id string; empty string = not set. */
  const [editedSupplierInnerIds, setEditedSupplierInnerIds] = useState([]);
  /** Optional user_ids for server-only PDF rows (parallel to serverOnlyFiles). */
  const [selectedServerUserIds, setSelectedServerUserIds] = useState([]);
  /** Track requisition input touch state to avoid noisy invalid UI on first render. */
  const [touchedRequisitions, setTouchedRequisitions] = useState([]);
  /** Track supplier select touch state to avoid noisy invalid UI on first render. */
  const [touchedSuppliers, setTouchedSuppliers] = useState([]);
  /** Tracks whether submit was attempted, used to reveal validation cues. */
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const hasServerOnly = Array.isArray(serverOnlyFiles) && serverOnlyFiles.length > 0;
  const hasClientPayloads = Array.isArray(quotePayloads) && quotePayloads.length > 0;

  useEffect(() => {
    if (!token) {
      setUserSelectList([]);
      setIsLoadingUsers(false);
      return;
    }
    setIsLoadingUsers(true);
    getUsersForOrder(token)
      .then(users => {
        if (Array.isArray(users) && !users.error) {
          const droppedUsers = [];
          const options = users.reduce((acc, user) => {
            const mappedOption = mapUserToOption(user);
            if (mappedOption) {
              acc.push(mappedOption);
            } else {
              droppedUsers.push({
                id: user?.id ?? null,
                first_name: user?.first_name ?? null,
                last_name: user?.last_name ?? null,
                email: user?.email ?? null,
                username: user?.username ?? null,
              });
            }
            return acc;
          }, []);
          setUserSelectList(options);
          if (droppedUsers.length > 0) {
            logWarn(LOG_CONTEXT, 'Dropped invalid users in review step', {
              droppedCount: droppedUsers.length,
              droppedUsers,
            });
          }
          logInfo(LOG_CONTEXT, 'Users loaded for review step', { count: users.length });
        } else {
          setUserSelectList([]);
          logWarn(LOG_CONTEXT, 'Users load failed or empty', { error: users?.error });
        }
      })
      .catch(err => {
        setUserSelectList([]);
        logWarn(LOG_CONTEXT, 'Users load error', { message: err?.message });
      })
      .finally(() => setIsLoadingUsers(false));
  }, [token]);

  useEffect(() => {
    if (!token) {
      setSupplierList([]);
      setIsLoadingSuppliers(false);
      return;
    }
    setIsLoadingSuppliers(true);
    getSupplierSelectList(token, list => {
      const arr = Array.isArray(list) ? list : [];
      setSupplierList(arr);
      logInfo(LOG_CONTEXT, 'Suppliers loaded for review step', { count: arr.length });
      setIsLoadingSuppliers(false);
    })
      .then(result => {
        if (!result?.success) setIsLoadingSuppliers(false);
      })
      .catch(() => setIsLoadingSuppliers(false));
  }, [token]);

  const supplierDropdownOptions = useMemo(() => {
    const rows = (supplierList || [])
      .map(s => ({
        value: String(s.inner_supplier_id ?? ''),
        label: s.label || String(s.inner_supplier_id ?? ''),
      }))
      .filter(o => o.value);
    return [{ value: '', label: 'Select supplier…' }, ...rows];
  }, [supplierList]);

  const userDropdownOptions = useMemo(
    () => withOptionalNoUser(userSelectList),
    [userSelectList]
  );

  const projectDropdownOptions = useMemo(
    () => [NO_PROJECT_OPTION, ...(projectOptions || [])],
    [projectOptions]
  );

  /**
   * Resolve supplier master row by Excel/file inner supplier ID (string vs number safe).
   */
  const lookupSupplierByInnerId = useMemo(() => {
    const map = new Map();
    (supplierList || []).forEach(s => {
      const id = s.inner_supplier_id;
      if (id != null && String(id).trim() !== '') {
        map.set(String(id).trim(), s);
      }
    });
    return innerId => {
      if (innerId == null || String(innerId).trim() === '') return null;
      return map.get(String(innerId).trim()) ?? null;
    };
  }, [supplierList]);

  useEffect(() => {
    setSelectedUserIds(quotePayloads?.length ? quotePayloads.map(() => null) : []);
  }, [quotePayloads]);

  useEffect(() => {
    const prefilled =
      defaultProjectId != null && String(defaultProjectId).trim() !== ''
        ? defaultProjectId
        : null;
    setSelectedProjectIds(
      quotePayloads?.length ? quotePayloads.map(() => prefilled) : []
    );
  }, [quotePayloads, defaultProjectId]);

  useEffect(() => {
    setSelectedServerUserIds(
      serverOnlyFiles?.length ? serverOnlyFiles.map(() => null) : []
    );
  }, [serverOnlyFiles]);

  useEffect(() => {
    if (!quotePayloads?.length) {
      setEditedRequisitions([]);
      setEditedSupplierInnerIds([]);
      setTouchedRequisitions([]);
      setTouchedSuppliers([]);
      setSubmitAttempted(false);
      return;
    }
    setEditedRequisitions(
      quotePayloads.map(q => String(q.requisition_number ?? ''))
    );
    setEditedSupplierInnerIds(
      quotePayloads.map(q =>
        q.supplier_id != null && String(q.supplier_id).trim() !== ''
          ? String(q.supplier_id).trim()
          : ''
      )
    );
    setTouchedRequisitions(quotePayloads.map(() => false));
    setTouchedSuppliers(quotePayloads.map(() => false));
    setSubmitAttempted(false);
  }, [quotePayloads]);

  const setUserForIndex = (index, userId) => {
    setSelectedUserIds(prev => {
      const next = [...prev];
      next[index] = userId;
      return next;
    });
  };

  const setProjectForIndex = (index, projectId) => {
    setSelectedProjectIds(prev => {
      const next = [...prev];
      next[index] = projectId;
      return next;
    });
  };

  const setServerUserAt = (index, userId) => {
    setSelectedServerUserIds(prev => {
      const next = [...prev];
      next[index] = userId;
      return next;
    });
  };

  const setRequisitionAt = (index, value) => {
    setEditedRequisitions(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const setSupplierInnerIdAt = (index, innerId) => {
    setEditedSupplierInnerIds(prev => {
      const next = [...prev];
      next[index] = innerId != null && innerId !== undefined ? String(innerId) : '';
      return next;
    });
  };

  const markRequisitionTouched = index => {
    setTouchedRequisitions(prev => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  const markSupplierTouched = index => {
    setTouchedSuppliers(prev => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  /**
   * Required-field validation for client-parsed quote rows.
   * Each row must include a non-empty requisition and supplier inner id.
   */
  const requiredFieldChecks = useMemo(() => {
    return (quotePayloads || []).map((payload, index) => {
      const requisitionValue =
        editedRequisitions[index] != null
          ? String(editedRequisitions[index]).trim()
          : String(payload?.requisition_number ?? '').trim();
      const supplierValue =
        editedSupplierInnerIds[index] != null
          ? String(editedSupplierInnerIds[index]).trim()
          : String(payload?.supplier_id ?? '').trim();
      return {
        requisitionMissing: requisitionValue === '',
        supplierMissing: supplierValue === '',
      };
    });
  }, [quotePayloads, editedRequisitions, editedSupplierInnerIds]);

  const missingRequiredCount = useMemo(() => {
    return requiredFieldChecks.reduce((sum, row) => {
      return (
        sum + (row.requisitionMissing ? 1 : 0) + (row.supplierMissing ? 1 : 0)
      );
    }, 0);
  }, [requiredFieldChecks]);

  const canConfirmRequired = !hasClientPayloads || missingRequiredCount === 0;
  const canConfirm =
    canConfirmRequired &&
    !disabled &&
    !isSubmitting &&
    !isLoadingUsers &&
    !(hasClientPayloads && isLoadingSuppliers);

  const handleConfirm = () => {
    setSubmitAttempted(true);
    if (!canConfirmRequired) {
      logWarn(LOG_CONTEXT, 'Create quotes blocked due to missing required fields', {
        missingRequiredCount,
        requiredFieldChecks,
      });
      return;
    }
    const quotesWithUsers = (quotePayloads || []).map((payload, i) => {
      const reqRaw = editedRequisitions[i] != null
        ? String(editedRequisitions[i]).trim()
        : String(payload.requisition_number ?? '').trim();
      const requisition_number =
        reqRaw || String(payload.requisition_number ?? '').trim();
      const sid = editedSupplierInnerIds[i];
      const supplier_id =
        sid != null && String(sid).trim() !== ''
          ? String(sid).trim()
          : null;
      return {
        ...payload,
        requisition_number,
        supplier_id,
        supplier_name_text: supplier_id ? undefined : payload.supplier_name_text,
        user_id: selectedUserIds[i] || null,
        project_id: selectedProjectIds[i] ?? null,
      };
    });
    const withUser = quotesWithUsers.filter(q => q.user_id != null).length;
    const withProject = quotesWithUsers.filter(q => q.project_id != null).length;
    logInfo(LOG_CONTEXT, 'Create quotes confirmed', {
      quotesCount: quotesWithUsers.length,
      quotesWithUserAssigned: withUser,
      quotesWithProjectAssigned: withProject,
      serverOnlyCount: hasServerOnly ? serverOnlyFiles.length : 0,
      serverUsersAssigned: hasServerOnly
        ? selectedServerUserIds.filter(u => u != null).length
        : 0,
    });
    if (hasServerOnly) {
      onConfirm(quotesWithUsers, { serverUserIds: selectedServerUserIds });
    } else {
      onConfirm(quotesWithUsers);
    }
  };

  if (!hasClientPayloads && !hasServerOnly) return null;

  const defaultHeading =
    'Review quotes and assign project / user (optional)';

  return (
    <div className="quote-upload-review-step mt-3" dir="ltr">
      <h6 className="d-flex align-items-center gap-2 mb-2">
        <PersonPlus size={16} className="text-primary" />
        {reviewHeading || defaultHeading}
      </h6>
      <div className="small text-muted mb-2">
        Use <strong>No project</strong> or <strong>No user</strong> when a row should
        not be linked to a project or email recipient.
      </div>
      {hasClientPayloads && (
        <div className="small text-muted mb-2 quote-review-required-hint">
          Fields marked <span className="text-danger ms-1">*</span> are required
          to create quotes.
        </div>
      )}
      {hasServerOnly && (
        <Alert variant="info" className="py-2 small mb-3">
          {serverOnlyFiles.length === 1
            ? 'Demand was not detected in 1 file. Line items will be extracted on the server after upload.'
            : `Demand was not detected in ${serverOnlyFiles.length} files. Line items will be extracted on the server after upload.`}
        </Alert>
      )}
      {hasServerOnly && (
        <ListGroup className="mb-3">
          {serverOnlyFiles.map((file, index) => (
            <ListGroup.Item key={`server-only-${file.name}-${index}`} className="border rounded mb-3 p-3" dir="ltr">
              <div className="d-flex flex-column flex-md-row align-items-start gap-3 justify-content-between">
                <div className="flex-grow-1 min-width-0">
                  <div className="small text-muted mb-0">PDF (server extraction)</div>
                  <div className="small fw-medium text-truncate" title={file.name}>
                    {file.name}
                  </div>
                  <Badge bg="secondary" className="mt-2">Processing on server</Badge>
                </div>
                <div className="d-flex flex-column" style={{ minWidth: 220 }}>
                  <span className="small text-muted mb-0">User (optional)</span>
                  {isLoadingUsers ? (
                    <Spinner animation="border" size="sm" className="mt-2" />
                  ) : (
                    <div className="mt-1">
                      <DropdownSelect
                        optionsList={userDropdownOptions}
                        label=""
                        placeholder="Select user for email updates"
                        selectedValue={findUserOption(
                          userDropdownOptions,
                          selectedServerUserIds[index]
                        )}
                        setSelectedValue={option => {
                          const v = option?.value;
                          setServerUserAt(
                            index,
                            option == null || v === '' ? null : v
                          );
                        }}
                        disabledOptions={[]}
                        disabled={disabled || isSubmitting}
                      />
                    </div>
                  )}
                </div>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {hasClientPayloads && (
        <>
          {hasServerOnly && (
            <h6 className="small text-muted mb-2">
              Client-parsed lines (demand found in PDF text)
            </h6>
          )}
          <ListGroup className="mb-3">
            {(quotePayloads || []).map((quote, index) => {
              const displayInnerId =
                editedSupplierInnerIds.length > index &&
                String(editedSupplierInnerIds[index] ?? '').trim() !== ''
                  ? String(editedSupplierInnerIds[index]).trim()
                  : String(quote.supplier_id ?? '').trim();
              const matchedSupplier = displayInnerId
                ? lookupSupplierByInnerId(displayInnerId)
                : null;
              const supplierSelectValue =
                supplierDropdownOptions.find(
                  o => String(o.value) === String(displayInnerId)
                ) || null;

              return (
          <ListGroup.Item key={index} className="border rounded mb-3 p-3" dir="ltr">
            {(quote.demand_parse_fallback === true ||
              (quote.source_file_name &&
                String(quote.requisition_number) === String(quote.source_file_name))) && (
              <Alert variant="warning" className="py-2 small mb-2">
                Demand number was not detected in the PDF text; the value shown may be the file
                name. Edit the requisition field below before creating quotes.
              </Alert>
            )}
            <div className="d-flex flex-column flex-lg-row align-items-start gap-3 mb-2">
              <div className="flex-grow-1 w-100" style={{ minWidth: 0, maxWidth: 360 }}>
                <Form.Label className="small text-muted mb-0">Requisition / demand</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={
                    editedRequisitions.length > index
                      ? editedRequisitions[index]
                      : String(quote.requisition_number ?? '')
                  }
                  onChange={e => setRequisitionAt(index, e.target.value)}
                  onBlur={() => markRequisitionTouched(index)}
                  disabled={disabled || isSubmitting}
                  className="mt-1"
                  isInvalid={
                    (submitAttempted || touchedRequisitions[index]) &&
                    requiredFieldChecks[index]?.requisitionMissing
                  }
                />
                <div className="small mt-1 quote-review-required-label">
                  <span className="text-muted">
                    Requisition / demand <span className="text-danger ms-1">*</span>
                  </span>
                </div>
                {(submitAttempted || touchedRequisitions[index]) &&
                  requiredFieldChecks[index]?.requisitionMissing && (
                    <div className="small text-danger mt-1 quote-review-required-error">
                      Requisition / demand is required.
                    </div>
                  )}
              </div>
              <div className="flex-grow-1 w-100" style={{ minWidth: 0, maxWidth: 400 }}>
                <Form.Label className="small text-muted mb-0">
                  Supplier (inner ID) <span className="text-danger ms-1">*</span>
                </Form.Label>
                {(displayInnerId !== '' || quote.supplier_name_text) && (
                  <Alert variant="light" className="py-2 small mb-2 border">
                    {displayInnerId !== '' && (
                      <div className="mb-0">
                        <strong>From file — Inner supplier ID:</strong> {displayInnerId}
                      </div>
                    )}
                    {quote.supplier_name_text ? (
                      <div className={displayInnerId !== '' ? 'mt-1 mb-0' : 'mb-0'}>
                        <strong>From file — Supplier name:</strong> {quote.supplier_name_text}
                      </div>
                    ) : null}
                    {displayInnerId !== '' &&
                      (matchedSupplier ? (
                        <div className="mt-1 mb-0 small text-success">
                          <strong>Matched in system:</strong> {matchedSupplier.label}
                        </div>
                      ) : (
                        <div className="mt-1 mb-0 small text-warning">
                          Inner ID not found in supplier list—pick supplier below or fix master data.
                        </div>
                      ))}
                  </Alert>
                )}
                {isLoadingSuppliers ? (
                  <Spinner animation="border" size="sm" className="mt-2" />
                ) : (
                  <div className="mt-1">
                    <DropdownSelect
                      optionsList={supplierDropdownOptions}
                      label=""
                      placeholder="Select supplier"
                      selectedValue={supplierSelectValue}
                      setSelectedValue={option =>
                        setSupplierInnerIdAt(index, option?.value ?? '')
                      }
                      onBlur={() => markSupplierTouched(index)}
                      disabledOptions={[]}
                      disabled={disabled || isSubmitting}
                    />
                  </div>
                )}
                {(submitAttempted || touchedSuppliers[index]) &&
                  requiredFieldChecks[index]?.supplierMissing && (
                    <div className="small text-danger mt-1 quote-review-required-error">
                      Supplier (inner ID) is required.
                    </div>
                  )}
              </div>
              <div className="d-flex flex-column align-items-stretch quote-review-assignment-column">
                <span className="small text-muted mb-0">Project (optional)</span>
                <div className="mt-1">
                  <DropdownSelect
                    optionsList={projectDropdownOptions}
                    label=""
                    placeholder="No project"
                    selectedValue={findProjectOption(
                      projectDropdownOptions,
                      selectedProjectIds[index]
                    )}
                    setSelectedValue={option => {
                      const v = option?.value;
                      setProjectForIndex(
                        index,
                        option == null || v === '' ? null : v
                      );
                    }}
                    disabledOptions={[]}
                    disabled={disabled || isSubmitting}
                    clearable
                  />
                </div>
              </div>
              <div className="d-flex flex-column align-items-stretch quote-review-assignment-column">
                <span className="small text-muted mb-0">User (optional)</span>
                {isLoadingUsers ? (
                  <Spinner animation="border" size="sm" className="mt-2" />
                ) : (
                  <div className="mt-1">
                    <DropdownSelect
                      optionsList={userDropdownOptions}
                      label=""
                      placeholder="Select user for email updates"
                      selectedValue={findUserOption(
                        userDropdownOptions,
                        selectedUserIds[index]
                      )}
                      setSelectedValue={option => {
                        const v = option?.value;
                        setUserForIndex(
                          index,
                          option == null || v === '' ? null : v
                        );
                      }}
                      disabledOptions={[]}
                      disabled={disabled || isSubmitting}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="mb-2">
              <Badge bg="secondary">
                {Array.isArray(quote.items) ? quote.items.length : 0} line(s) in table
              </Badge>
            </div>
            {Array.isArray(quote.items) && quote.items.length > 0 && (
              <div className="table-responsive mt-2" dir="ltr">
                <Table size="sm" bordered className="mb-0 small w-100" style={{ minWidth: 480 }} dir="ltr">
                  <thead className="table-light">
                    <tr>
                      <th>Catalog #</th>
                      <th>Product / description</th>
                      <th>Qty</th>
                      <th>Unit price</th>
                      <th>Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.items.map((item, itemIdx) => (
                      <tr key={itemIdx}>
                        <td className="text-truncate" dir="auto" title={String(item.supplier_catalog_number ?? '')}>{item.supplier_catalog_number ?? '—'}</td>
                        <td className="text-truncate" dir="auto" title={String(item.product_name ?? '')}>{item.product_name ?? '—'}</td>
                        <td dir="ltr">{item.quantity ?? '—'}</td>
                        <td dir="ltr">{item.unit_price != null ? item.unit_price : '—'}</td>
                        <td dir="ltr">{item.currency ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </ListGroup.Item>
              );
            })}
          </ListGroup>
        </>
      )}

      {submitError && (
        <Alert variant="danger" className="py-2 mb-2">
          <div className="d-flex align-items-start gap-2">
            <XCircle size={16} className="mt-1 flex-shrink-0" />
            <span className="small" style={{ whiteSpace: 'pre-line' }}>
              {submitError}
            </span>
          </div>
        </Alert>
      )}

      {isSubmitting && submitStatusMessage && (
        <Alert variant="secondary" className="py-2 mb-2">
          <div className="d-flex align-items-center gap-2 small">
            <Spinner animation="border" size="sm" />
            <span>{submitStatusMessage}</span>
          </div>
        </Alert>
      )}

      {!canConfirmRequired && hasClientPayloads && (
        <Alert variant="warning" className="py-2 mb-2 quote-review-blocked-alert">
          <div className="small">
            Complete all required fields before creating quotes. Missing fields:{' '}
            {missingRequiredCount}
          </div>
        </Alert>
      )}

      <div className="d-flex flex-wrap align-items-center gap-2">
        <Button
          variant="success"
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          {isSubmitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Creating...
            </>
          ) : (
            <>
              <CheckCircle size={16} className="me-2" />
              {confirmLabel}
            </>
          )}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onCancel}
          disabled={disabled || isSubmitting}
        >
          Back to files
        </Button>
        <Button
          variant="link"
          size="sm"
          className="text-muted ms-auto"
          onClick={() => downloadLogFile()}
          disabled={disabled || isSubmitting}
        >
          Download log file
        </Button>
      </div>
    </div>
  );
};

export default QuoteUploadReviewStep;
